import { TokenBalanceList } from "@shared/components/WalletMenu/TokenBalanceList";
import { useTokenBalancesForAddress } from "@shared/wallet/hooks/useUsdvBalanceForAddress";
import { SignatureIcon } from "@solomon-labs/design/components/icons";
import {
    Button,
    Label,
    Switch,
    Text
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";

import { useRetailAccountStore } from "@/hooks/yaas/retailAccountStore";
import { useRetailAccount } from "@/hooks/yaas/useRetailAccount";
import { useRetailSquadsDiscovery } from "@/hooks/yaas/useRetailSquadsDiscovery";

import { BalanceLoadNotice } from "../sections/BalanceLoadNotice";

import { SquadsAccountPanel } from "./SquadsAccountPanel";

/** How long a failed or empty Autocheck result shows before reverting. */
const AUTOCHECK_RESULT_MS = 2000;
// Keep the implementation for a later rollout; manual verification stays available.
const SHOW_AUTOCHECK = false;

type AutocheckResult = "Failed" | "Incomplete" | "None found";

interface RetailAccountSelectorProps {
    children: React.ReactNode;
    /** The wallet address row; pass an icon to replace the wallet's own. */
    renderWalletRow: (icon?: React.ReactNode) => React.ReactNode;
    isOpen?: boolean;
}

export function RetailAccountSelector({
    children,
    renderWalletRow,
    isOpen = true
}: RetailAccountSelectorProps): React.ReactElement | null {
    const account = useRetailAccount();
    const store = useRetailAccountStore();
    const [addingMultisig, setAddingMultisig] = useState(false);
    const showMultisig = account.isSquads || addingMultisig;
    const discovery = useRetailSquadsDiscovery(
        account.signerAddress,
        showMultisig && isOpen
    );
    const selectDiscovered = useRef(false);
    useEffect(() => {
        if (!isOpen) selectDiscovered.current = false;
        if (!isOpen || account.isSquads) setAddingMultisig(false);
    }, [isOpen, account.isSquads]);
    const wasChecking = useRef(false);
    const [autocheckResult, setAutocheckResult] =
        useState<AutocheckResult | null>(null);

    // Flash the outcome of a finished check, then fall back to "Autocheck".
    useEffect(() => {
        if (discovery.isFetching) {
            wasChecking.current = true;
            setAutocheckResult(null);
            return;
        }
        if (!wasChecking.current) return;
        wasChecking.current = false;
        const found = discovery.data?.multisigs.length ?? 0;
        if (discovery.isError) setAutocheckResult("Failed");
        else if (discovery.data?.partial) setAutocheckResult("Incomplete");
        else if (discovery.isSuccess && found === 0)
            setAutocheckResult("None found");
    }, [
        discovery.isFetching,
        discovery.isError,
        discovery.isSuccess,
        discovery.data
    ]);

    useEffect(() => {
        if (!autocheckResult) return;
        const timer = setTimeout(
            () => setAutocheckResult(null),
            AUTOCHECK_RESULT_MS
        );
        return (): void => clearTimeout(timer);
    }, [autocheckResult]);

    const autocheckLabel = discovery.isFetching
        ? "Autochecking..."
        : (autocheckResult ?? "Autocheck");

    const { select: selectAccount } = store;
    useEffect(() => {
        if (!showMultisig || !selectDiscovered.current || !discovery.isSuccess)
            return;
        selectDiscovered.current = false;
        if (!account.isSquads && discovery.data.multisigs[0]) {
            selectAccount(account.signerAddress, discovery.data.multisigs[0]);
        }
    }, [
        showMultisig,
        discovery.isSuccess,
        discovery.data,
        account.isSquads,
        account.signerAddress,
        selectAccount
    ]);

    const remembered = store.remembered[account.signerAddress] ?? [];
    const discovered = discovery.data?.multisigs ?? [];
    const savedSelection = store.selected[account.signerAddress];
    const selectedEntry = savedSelection
        ? {
              ...savedSelection,
              vaultAddress: account.vaultAddress || savedSelection.vaultAddress
          }
        : null;
    const entries = Array.from(
        new Map(
            [
                ...remembered,
                ...discovered,
                ...(selectedEntry ? [selectedEntry] : [])
            ].map((entry) => [entry.address, entry])
        ).values()
    );
    const selectedVault =
        account.isSquads && account.ready ? account.vaultAddress : null;
    const vaultBalances = useTokenBalancesForAddress(selectedVault);

    if (!account.signerAddress) return null;

    return (
        <>
            <div className="flex flex-col gap-1 px-4">
                {/* In multisig mode the connected wallet signs for the
                    multisig, so it wears the signature mark (as in the nav). */}
                {renderWalletRow(
                    showMultisig ? (
                        <SignatureIcon
                            aria-hidden="true"
                            className="size-5 shrink-0 text-foreground-base"
                        />
                    ) : undefined
                )}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Switch
                            id="retail-multisig-mode"
                            // A native button keeps drawer swipe handling from capturing switch clicks.
                            nativeButton
                            render={<button type="button" />}
                            size="sm"
                            checked={showMultisig}
                            onCheckedChange={(checked) => {
                                selectDiscovered.current = checked;
                                setAddingMultisig(checked);
                                store.select(
                                    account.signerAddress,
                                    checked ? (entries[0] ?? null) : null
                                );
                            }}
                        />
                        <Label htmlFor="retail-multisig-mode">
                            {showMultisig
                                ? "Multisig mode on"
                                : "Multisig mode off"}
                        </Label>
                    </div>
                    {SHOW_AUTOCHECK && showMultisig && (
                        <div role="status" aria-live="polite">
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={autocheckLabel}
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            duration: 0.15,
                                            ease: "easeOut"
                                        }
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: {
                                            duration: 0.1,
                                            ease: "easeIn"
                                        }
                                    }}
                                >
                                    {autocheckLabel === "Autocheck" ? (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => {
                                                selectDiscovered.current = true;
                                                // Re-verify the selected vault too, so a
                                                // membership change is picked up.
                                                if (account.isSquads)
                                                    account.retry();
                                                void discovery.refetch();
                                            }}
                                        >
                                            Autocheck
                                        </Button>
                                    ) : (
                                        <Text
                                            variant="small"
                                            className={
                                                autocheckLabel === "Failed"
                                                    ? "text-error-base"
                                                    : "text-foreground-muted"
                                            }
                                        >
                                            {autocheckLabel}
                                        </Text>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>
            {showMultisig && (
                <SquadsAccountPanel
                    account={account}
                    discovery={discovery}
                    entries={entries}
                    onStopAutoSelect={() => {
                        selectDiscovered.current = false;
                    }}
                />
            )}
            {showMultisig ? (
                <div className="px-4">
                    <TokenBalanceList
                        balances={vaultBalances.balances}
                        isLoading={vaultBalances.isLoading}
                        unavailable={
                            !selectedVault || !vaultBalances.hasFetched
                        }
                    />
                    {selectedVault &&
                        vaultBalances.error &&
                        !vaultBalances.hasFetched && (
                            <BalanceLoadNotice
                                onRetry={vaultBalances.refetch}
                                retrying={vaultBalances.isFetching}
                            />
                        )}
                </div>
            ) : (
                children
            )}
        </>
    );
}
